import React, { useState, useEffect } from 'react';
import { Card, Table, Select, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import Page from '../../components/layout/Page';
import { getBankAnalytics } from '../../services/analyticsService';
import { formatCurrency } from '../../utils/formatters';

import { API_BASE_URL } from '../../config';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Title } = Typography;

const BankAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const years = Array.from({ length: 5 }, (_, index) => ({
    value: new Date().getFullYear() - index,
    label: String(new Date().getFullYear() - index)
  }));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getBankAnalytics(selectedYear);
        setData(response.analytics);
      } catch (error) {
        console.error('Error fetching bank analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear]);

  const columns = [
    {
      title: t('analytics.bank'),
      dataIndex: 'bank__name',
      key: 'bank',
      sorter: (a, b) => a.bank__name.localeCompare(b.bank__name),
    },
    {
      title: t('analytics.pooler'),
      dataIndex: 'pooler__name',
      key: 'pooler',
      sorter: (a, b) => {
        if (!a.pooler__name) return 1;
        if (!b.pooler__name) return -1;
        return a.pooler__name.localeCompare(b.pooler__name);
      },
      render: (text) => text || '-',
    },
    {
      title: t('analytics.totalLoanAmount'),
      dataIndex: 'total_provision',
      key: 'total_provision',
      align: 'right',
      sorter: (a, b) => a.total_provision - b.total_provision,
      render: (value) => formatCurrency(value),
    },    
    {
      title: t('analytics.loanCount'),
      dataIndex: 'loan_count',
      key: 'loan_count',
      align: 'right',
      sorter: (a, b) => a.loan_count - b.loan_count,
    },
    {
      title: t('analytics.avgLoanAmount'),
      dataIndex: 'avg_loan_amount',
      key: 'avg_loan_amount',
      align: 'right',
      sorter: (a, b) => a.avg_loan_amount - b.avg_loan_amount,
      render: (value) => formatCurrency(value),
    },
    {
      title: t('analytics.avgProvisionPercentage'),
      dataIndex: 'avg_provision_percentage',
      key: 'avg_provision_percentage',
      align: 'right',
      sorter: (a, b) => a.avg_provision_percentage - b.avg_provision_percentage,
      render: (value) => value?.toFixed(2) + '%',
    },
    {
      title: t('analytics.totalEvaluation'),
      dataIndex: 'total_evaluation_amount',
      key: 'total_evaluation_amount',
      align: 'right',
      sorter: (a, b) => a.total_evaluation_amount - b.total_evaluation_amount,
      render: (value) => formatCurrency(value),
    },
  ];

  return (
    <Page
      title={t('analytics.bankAnalytics')}
      subtitle={t('analytics.bankAnalyticsSubtitle')}
      menu={[
        {
          key: 'analytics',
          label: t('analytics.title'),
          path: '/analytics'
        },
        {
          key: 'bank-analytics',
          label: t('analytics.bankAnalytics'),
          path: '/bank-analytics'
        }
      ]}
    >
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Select
            value={selectedYear}
            onChange={setSelectedYear}
            style={{ width: 120 }}
          >
            {years.map(year => (
              <Option key={year.value} value={year.value}>
                {year.label}
              </Option>
            ))}
          </Select>
        </div>

        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey={(record) => `${record.bank__name}-${record.pooler__name}`}
          scroll={{ x: true }}
          summary={(pageData) => {
            const totalLoans = pageData.reduce((sum, row) => sum + row.loan_count, 0);
            const totalProvision = pageData.reduce((sum, row) => sum + row.total_provision, 0);
            const totalEvaluation = pageData.reduce((sum, row) => sum + row.total_evaluation_amount, 0);
            const avgLoanAmount = pageData.reduce((sum, row) => sum + row.avg_loan_amount, 0) / pageData.length;

            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <strong>{t('analytics.total')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>{formatCurrency(totalProvision)}</strong>
                  </Table.Summary.Cell>                    
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{totalLoans}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{formatCurrency(avgLoanAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong>-</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong>{formatCurrency(totalEvaluation)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    </Page>
  );
};

export default BankAnalytics;
